import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Dumbbell, Users } from 'lucide-react';
import { suggestNextWorkout } from '@/templates';
import { useExercises } from '@/context/ExerciseContext';

interface WorkoutTypeSelectionProps {
  templateType: 'upperLower' | 'fullBody';
  onSelectWorkoutType: (workoutType: 'upperA' | 'upperB' | 'lowerA' | 'lowerB' | 'fullBodyA' | 'fullBodyB') => void;
  onBack: () => void;
}

// Template history utilities
const getTemplateHistory = () => {
  try {
    const history = JSON.parse(localStorage.getItem('templateHistory') || '{}');
    return history.lastUsed || {};
  } catch (e) {
    return {};
  }
};

const getLastUsedDisplay = (templateType: string): string => {
  const history = getTemplateHistory();
  const lastUsed = history[templateType];
  
  if (!lastUsed) return 'Never used';
  
  const daysAgo = Math.floor((Date.now() - new Date(lastUsed).getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysAgo === 0) return 'Used today';
  if (daysAgo === 1) return 'Used yesterday'; 
  if (daysAgo < 7) return `${daysAgo} days ago`;
  if (daysAgo < 30) return `${Math.floor(daysAgo / 7)} weeks ago`;
  return 'Long ago';
};

export default function WorkoutTypeSelection({ 
  templateType,
  onSelectWorkoutType, 
  onBack 
}: WorkoutTypeSelectionProps) {
  const { getTodayExercises } = useExercises();

  // Get AI suggestion for next workout (upper vs lower)
  const todayExercises = getTodayExercises();
  const recentLogs = todayExercises.slice(0, 10);
  const suggestion = suggestNextWorkout(recentLogs);

  if (templateType === 'upperLower') {
    const workoutOptions = [
      {
        type: 'upperA' as const,
        title: 'Upper Body A',
        description: 'Machine-focused • Mid-chest emphasis',
        icon: Dumbbell,
        exercises: 7,
        lastUsed: getLastUsedDisplay('upperA'),
        isRecommended: suggestion.suggested === 'upper'
      },
      {
        type: 'upperB' as const,
        title: 'Upper Body B', 
        description: 'Free weight focused • Upper chest emphasis',
        icon: Dumbbell,
        exercises: 7,
        lastUsed: getLastUsedDisplay('upperB'),
        isRecommended: suggestion.suggested === 'upper'
      },
      {
        type: 'lowerA' as const,
        title: 'Lower Body A',
        description: 'Quad dominant • Machine-focused',
        icon: Users,
        exercises: 6,
        lastUsed: getLastUsedDisplay('lowerA'),
        isRecommended: suggestion.suggested === 'lower'
      },
      {
        type: 'lowerB' as const,
        title: 'Lower Body B',
        description: 'Hip dominant • Unilateral movements',
        icon: Users,
        exercises: 6,
        lastUsed: getLastUsedDisplay('lowerB'),
        isRecommended: suggestion.suggested === 'lower'
      }
    ];

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold tracking-tight mb-2">Choose Today's Focus</h2>
          <p className="text-muted-foreground">
            Select your workout variation
          </p>
        </div>

        {/* AI Recommendation */}
        {suggestion && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span className="font-medium text-blue-900 dark:text-blue-100">
                AI Recommendation: {suggestion.suggested === 'upper' ? 'Upper Body' : 'Lower Body'}
              </span>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {suggestion.reason}
            </p>
          </motion.div>
        )}

        {/* Workout Type Options */}
        <div className="grid gap-4">
          {workoutOptions.map((option, index) => {
            const Icon = option.icon;
            
            return (
              <motion.div
                key={option.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    option.isRecommended 
                      ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20' 
                      : 'hover:border-primary/50'
                  }`}
                  onClick={() => onSelectWorkoutType(option.type)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        {option.title}
                        {option.isRecommended && (
                          <Badge variant="default" className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                            Recommended
                          </Badge>
                        )}
                      </CardTitle>
                    </div>
                    <CardDescription className="ml-11">
                      {option.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between ml-11">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {option.exercises} exercises
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          3 sets each
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {option.lastUsed}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  }

  // Full Body template selection
  const fullBodyOptions = [
    {
      type: 'fullBodyA' as const,
      title: 'Full Body A',
      description: 'Machine-focused • Beginner friendly',
      exercises: 6,
      lastUsed: getLastUsedDisplay('fullBodyA')
    },
    {
      type: 'fullBodyB' as const,
      title: 'Full Body B',
      description: 'Free weight focused • Compound movements',
      exercises: 6,
      lastUsed: getLastUsedDisplay('fullBodyB')
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      <div className="text-center">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Choose Your Full Body Workout</h2>
        <p className="text-muted-foreground">
          Select your workout variation
        </p>
      </div>

      {/* Full Body Options */}
      <div className="grid gap-4">
        {fullBodyOptions.map((option, index) => (
          <motion.div
            key={option.type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Card 
              className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50"
              onClick={() => onSelectWorkoutType(option.type)}
            >
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Dumbbell className="w-5 h-5 text-primary" />
                  </div>
                  {option.title}
                </CardTitle>
                <CardDescription className="ml-11">
                  {option.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="flex items-center justify-between ml-11">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {option.exercises} exercises
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      3 sets each
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {option.lastUsed}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}